import { createScopedLogger } from "@@common";
import { ConfigContext } from "@@components";
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { BundleModel } from "../Models/BundleModel";
import { IComponent } from "../Models/IComponent";
import { DESIGNER_PROTOCOL_VERSION, MESSAGE_TYPES, SHARED_COMPONENT_ROOT_TYPE, type DesignerMessage } from "../Types/DesignerMessages";
import { isTrustedDesignerMessage, resolveDesignerOrigin } from "../Utils/designerAuthentication";
import {
  applyBundleComponentUpdateToBundleModel,
  applyBundleComponentUpdateToDesignerModel,
  applyBundleMetadataUpdateToBundleModel,
} from "../Utils/DesignerBundleUpdateUtils";
import { IsDesignMode } from "../Utils/runtimeDetection";

export type DesignerModeContextType = {
  selectedPath: string[] | null;
  model: IComponent | null; // Component model for designer-preview route (ViewModel or SharedComponent)
  bundle: BundleModel | null; // Bundle provided by designer (if in designer mode)
  onSelectComponent?: (id: string, path: string[]) => void;
  onUpdateComponent?: (id: string, path: string[], updates: any) => void;
  onAddChild?: (parentPath: string[]) => void;
};

const DesignerModeContext = createContext<DesignerModeContextType | null>(null);
const logger = createScopedLogger("DesignerMode");

export function useDesignerMode() {
  return useContext(DesignerModeContext);
}

interface DesignerModeProviderProps {
  children: ReactNode;
}

export function DesignerModeProvider({ children }: DesignerModeProviderProps) {
  const config = useContext(ConfigContext);
  const navigate = useNavigate();
  const location = useLocation();
  const designerOrigin = resolveDesignerOrigin(config?.DESIGNER_ORIGIN) ?? "*";

  // Use global IsDesignMode constant (calculated once at module load)
  const [model, setModel] = useState<IComponent | null>(null);
  const [selectedPath, setSelectedPath] = useState<string[] | null>(null);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewRoutes, setViewRoutes] = useState<Array<{ viewId: string; routePath: string | null }>>([]);
  const [bundle, setBundle] = useState<BundleModel | null>(null);

  // Track whether we're in the middle of a commanded navigation (to avoid race conditions)
  const isCommandedNavigation = useRef(false);
  const previousLocationRef = useRef(location.pathname);
  // Mirror activeViewId into a ref so the click handler (registered once) always reads the latest value
  const activeViewIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeViewIdRef.current = activeViewId;
  }, [activeViewId]);

  // Send IFRAME_READY message on mount
  useEffect(() => {
    if (IsDesignMode) {
      logger.debug("Running in iframe - sending IFRAME_READY");
      window.parent.postMessage(
        { type: MESSAGE_TYPES.IFRAME_READY, payload: { protocolVersion: DESIGNER_PROTOCOL_VERSION } } satisfies DesignerMessage,
        designerOrigin
      );
    }
  }, [designerOrigin]);

  // Handle Ctrl+Click for component selection in designer mode
  useEffect(() => {
    if (!IsDesignMode) return;

    const handleClick = (event: MouseEvent) => {
      // Only handle Ctrl+Click or Cmd+Click (Mac)
      if (!event.ctrlKey && !event.metaKey) return;

      // Find the closest element with component data
      let target = event.target as HTMLElement | null;
      let foundComponent = false;
      let sharedComponentId: string | undefined;
      let referenceComponentPath: string[] | undefined;
      let referenceComponentId: string | undefined;

      // Walk up to find both the component and any parent SharedComponent reference
      let searchTarget = target;
      let portalParentId: string | undefined;
      let portalParentPath: string[] | undefined;

      while (searchTarget && searchTarget !== document.body) {
        // Check for portaled content (e.g., Popover) that has a link back to its parent
        const portalParent = searchTarget.getAttribute("data-portal-parent-id");
        if (portalParent && !portalParentId) {
          portalParentId = portalParent;
          const portalPathStr = searchTarget.getAttribute("data-portal-parent-path");
          if (portalPathStr) {
            try {
              portalParentPath = JSON.parse(portalPathStr) as string[];
            } catch (err) {
              logger.error("Failed to parse portal parent path:", portalPathStr, err);
            }
          }
        }

        // Check for SharedComponent reference (from ReferenceComponent)
        const scId = searchTarget.getAttribute("data-shared-component-id");

        if (scId && !sharedComponentId) {
          sharedComponentId = scId;
          // Also capture the ReferenceComponent's ID and path
          referenceComponentId = searchTarget.getAttribute("data-component-id") || undefined;
          const refPathStr = searchTarget.getAttribute("data-component-path");
          if (refPathStr) {
            try {
              referenceComponentPath = JSON.parse(refPathStr) as string[];
            } catch (err) {
              logger.error("Failed to parse reference component path:", refPathStr, err);
            }
          }
        }
        searchTarget = searchTarget.parentElement;
      }

      // If we found a portal parent but no SharedComponent, look up the portal parent to find SharedComponent context
      if (portalParentId && portalParentPath && !sharedComponentId) {
        // Find the portal parent element in the actual DOM
        const portalParentElement = document.querySelector(`[data-component-id="${portalParentId}"]`) as HTMLElement | null;
        if (portalParentElement) {
          // Walk up from the portal parent to find SharedComponent reference
          let parentSearch = portalParentElement.parentElement;
          while (parentSearch && parentSearch !== document.body) {
            const scId = parentSearch.getAttribute("data-shared-component-id");
            if (scId) {
              sharedComponentId = scId;
              referenceComponentId = parentSearch.getAttribute("data-component-id") || undefined;
              const refPathStr = parentSearch.getAttribute("data-component-path");
              if (refPathStr) {
                try {
                  referenceComponentPath = JSON.parse(refPathStr) as string[];
                } catch (err) {
                  logger.error("Failed to parse reference component path:", refPathStr, err);
                }
              }
              break;
            }
            parentSearch = parentSearch.parentElement;
          }
        }
      }

      // Now find the actual component that was clicked
      while (target && target !== document.body) {
        const componentId = target.getAttribute("data-component-id");
        const componentPathStr = target.getAttribute("data-component-path");

        if (componentId && componentPathStr) {
          event.preventDefault();
          event.stopPropagation();
          foundComponent = true;

          try {
            const path = JSON.parse(componentPathStr) as string[];

            // If the clicked component IS the ReferenceComponent itself,
            // don't include SharedComponent context (user wants to select the Reference)
            const isReferenceComponentItself = referenceComponentId && componentId === referenceComponentId;

            window.parent.postMessage(
              {
                type: MESSAGE_TYPES.COMPONENT_SELECTED,
                payload: {
                  id: componentId,
                  path,
                  viewId: activeViewIdRef.current || undefined,
                  sharedComponentId: isReferenceComponentItself ? undefined : sharedComponentId,
                  referenceComponentPath: isReferenceComponentItself ? undefined : referenceComponentPath,
                },
              },
              designerOrigin
            );
          } catch (err) {
            logger.error("Failed to parse component path:", componentPathStr, err);
          }

          break;
        }
        target = target.parentElement;
      }

      // If no component was found, user clicked outside - select the view itself
      if (!foundComponent && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
        window.parent.postMessage(
          { type: MESSAGE_TYPES.COMPONENT_SELECTED, payload: { id: "", path: [], viewId: activeViewIdRef.current || undefined } },
          designerOrigin
        );
      }
    };

    document.addEventListener("click", handleClick, true); // Use capture phase
    return () => document.removeEventListener("click", handleClick, true);
  }, [designerOrigin]);

  // Listen for messages from parent (app-portal)
  useEffect(() => {
    if (!IsDesignMode) return;

    const handleMessage = (event: MessageEvent<DesignerMessage>) => {
      if (!isTrustedDesignerMessage(event, config?.DESIGNER_ORIGIN)) return;
      const message = event.data;
      if (!message || typeof message !== "object") return;

      switch (message.type) {
        case MESSAGE_TYPES.SET_BUNDLE:
          logger.debug("Received SET_BUNDLE", message.payload.bundle);
          setBundle(message.payload.bundle);
          // Update view routes when bundle is set
          if (message.payload.bundle?.Views) {
            const routes = message.payload.bundle.Views.map((v: any) => ({
              viewId: v.Id,
              routePath: v.RoutePath || null,
            }));
            setViewRoutes(routes);
          }
          // Keep the model in sync with the updated bundle so property edits (SET_BUNDLE)
          // are reflected immediately in the preview.
          setModel((currentModel) => {
            if (!currentModel) return currentModel;

            // If previewing a SharedComponent, update from bundle.SharedComponents
            if ((currentModel as any).Type === SHARED_COMPONENT_ROOT_TYPE) {
              const updatedSC = (message.payload.bundle?.SharedComponents as any[])?.find((sc) => sc.Id === currentModel.Id);
              if (!updatedSC) return currentModel;
              return { ...currentModel, Children: [updatedSC.Component] };
            }

            // If previewing a View, update from bundle.Views
            if ((currentModel as any).Type === "View") {
              const updatedView = (message.payload.bundle?.Views as any[])?.find((v) => v.Id === currentModel.Id);
              if (!updatedView) return currentModel;
              logger.debug("Updated view model from bundle:", updatedView.Id, "Dialog.Actions:", updatedView.Dialog?.Actions?.length ?? 0);
              return updatedView;
            }

            return currentModel;
          });
          break;

        case MESSAGE_TYPES.BUNDLE_COMPONENT_UPDATED: {
          const { scope, ownerId, componentPath, component } = message.payload;
          const update = { scope, ownerId, componentPath, component };

          setBundle((currentBundle) => applyBundleComponentUpdateToBundleModel(currentBundle, update));
          setModel((currentModel) => applyBundleComponentUpdateToDesignerModel(currentModel, update));
          break;
        }

        case MESSAGE_TYPES.BUNDLE_METADATA_UPDATED:
          setBundle((currentBundle) => applyBundleMetadataUpdateToBundleModel(currentBundle, message.payload));
          break;

        case MESSAGE_TYPES.RENDER_VIEW:
          logger.debug("Received RENDER_VIEW", message.payload);
          isCommandedNavigation.current = true;
          setModel(message.payload.view);
          setSelectedPath(message.payload.selectedPath && message.payload.selectedPath.length > 0 ? message.payload.selectedPath : null);
          if (message.payload.view?.Id) {
            setActiveViewId(message.payload.view.Id);
          }

          if (location.pathname !== "/designer-preview") {
            navigate("/designer-preview");
          } else {
            // Already on designer-preview - send VIEW_ACTIVATED immediately
            logger.debug("Already on designer-preview - sending VIEW_ACTIVATED");
            if (message.payload.view?.Id) {
              window.parent.postMessage(
                {
                  type: MESSAGE_TYPES.VIEW_ACTIVATED,
                  payload: { viewId: message.payload.view.Id, currentPath: location.pathname },
                },
                designerOrigin
              );
            }
            isCommandedNavigation.current = false;
          }
          break;

        case MESSAGE_TYPES.NAVIGATE_TO_VIEW: {
          logger.debug("Received NAVIGATE_TO_VIEW", message.payload);
          const { viewId, routePath } = message.payload;
          isCommandedNavigation.current = true;
          setActiveViewId(viewId);
          setModel(null);

          if (location.pathname !== routePath) {
            navigate(routePath);
          } else {
            // Already on the correct route - send VIEW_ACTIVATED immediately
            logger.debug("Already on route", routePath, "- sending VIEW_ACTIVATED");
            window.parent.postMessage(
              {
                type: MESSAGE_TYPES.VIEW_ACTIVATED,
                payload: { viewId: viewId, currentPath: location.pathname },
              },
              designerOrigin
            );
            isCommandedNavigation.current = false;
          }
          break;
        }

        case MESSAGE_TYPES.NAVIGATE_TO_PATH: {
          logger.debug("Received NAVIGATE_TO_PATH", message.payload);
          isCommandedNavigation.current = true;
          const targetPath = message.payload.path;
          if (location.pathname !== targetPath) {
            navigate(targetPath);
          } else {
            logger.debug("Already on path", targetPath);
            isCommandedNavigation.current = false;
          }
          break;
        }

        case MESSAGE_TYPES.NAVIGATE_BACK:
          logger.debug("Received NAVIGATE_BACK");
          isCommandedNavigation.current = true;
          navigate(-1);
          break;

        case MESSAGE_TYPES.UPDATE_SELECTION:
          logger.debug("Received UPDATE_SELECTION", message.payload);
          setSelectedPath(message.payload.path.length > 0 ? message.payload.path : null);
          break;

        case MESSAGE_TYPES.SET_VIEW_ROUTES:
          logger.debug("Received SET_VIEW_ROUTES", message.payload);
          setViewRoutes(message.payload.viewRoutes);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate, location.pathname, designerOrigin, config?.DESIGNER_ORIGIN]);

  // Detect navigation and notify parent with active view ID
  useEffect(() => {
    if (!IsDesignMode) return;

    const currentPath = location.pathname;
    const locationChanged = previousLocationRef.current !== currentPath;

    // Only process if location actually changed OR we have a commanded navigation
    // This prevents false triggers when viewRoutes or other deps change
    if (!locationChanged && !isCommandedNavigation.current) {
      return;
    }

    previousLocationRef.current = currentPath;

    // Only model previews use an explicitly selected view rather than a matched URL.
    if (currentPath === "/designer-preview") {
      if (isCommandedNavigation.current && activeViewId) {
        window.parent.postMessage({ type: MESSAGE_TYPES.VIEW_ACTIVATED, payload: { viewId: activeViewId, currentPath } }, designerOrigin);
      }
      isCommandedNavigation.current = false;
      return;
    }

    if (viewRoutes.length === 0) {
      logger.debug("No view routes available yet, skipping route matching");
      isCommandedNavigation.current = false;
      return;
    }

    // Use the same ranked, complete-path matching as DynamicRouteContainer.
    const routes = viewRoutes.flatMap((view) => (view.routePath ? [{ path: view.routePath, viewId: view.viewId }] : []));
    const matches = matchRoutes(routes, currentPath);
    const matchingView = matches?.[matches.length - 1].route;

    if (matchingView) {
      const newViewId = matchingView.viewId;
      const shouldNotify = isCommandedNavigation.current || locationChanged || newViewId !== activeViewId;

      if (shouldNotify) {
        logger.debug("Sending VIEW_ACTIVATED:", currentPath, "\u2192", newViewId, isCommandedNavigation.current ? "(commanded)" : "(user navigation)");
        setActiveViewId(newViewId);
        window.parent.postMessage(
          {
            type: MESSAGE_TYPES.VIEW_ACTIVATED,
            payload: { viewId: newViewId, currentPath: currentPath },
          },
          designerOrigin
        );
      }
      isCommandedNavigation.current = false;
    } else {
      logger.debug("No matching view found for route:", currentPath);
      isCommandedNavigation.current = false;
    }
    // Note: viewRoutes intentionally NOT in deps — we access it but don't need to re-run on changes.
    // Adding it would cause excessive effect runs and navigation throttling when SET_BUNDLE updates routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, activeViewId, designerOrigin]);

  // Create edit mode context when in designer mode
  const designerModeContext: DesignerModeContextType | null = useMemo(() => {
    if (!IsDesignMode) return null;

    return {
      selectedPath,
      model,
      bundle,
      onSelectComponent: (id: string, path: string[]) => {
        window.parent.postMessage(
          { type: MESSAGE_TYPES.COMPONENT_SELECTED, payload: { id, path, viewId: activeViewIdRef.current || undefined } },
          designerOrigin
        );
      },
      onUpdateComponent: (id: string, path: string[], updates: any) => {
        window.parent.postMessage({ type: MESSAGE_TYPES.COMPONENT_UPDATED, payload: { id, path, updates } }, designerOrigin);
      },
      onAddChild: (parentPath: string[]) => {
        window.parent.postMessage({ type: MESSAGE_TYPES.ADD_COMPONENT, payload: { parentPath } }, designerOrigin);
      },
    };
  }, [selectedPath, model, bundle, designerOrigin]);

  // Provide DesignerModeContext only when in designer mode (iframe)
  return <DesignerModeContext.Provider value={designerModeContext}>{children}</DesignerModeContext.Provider>;
}

export default DesignerModeContext;
